import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const formatDate = (dateString) => {
  if (!dateString) return 'Not started';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const percent = (value) => `${Number(value || 0).toFixed(1)}%`;

const statusStyles = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-amber-100 text-amber-800',
  completed: 'bg-purple-100 text-purple-700',
};

const TestCard = ({ test, onView, onPause, onComplete, onDelete }) => {
  const [copied, setCopied] = useState(false);
  const isFormExperiment = test?.testKind === 'form';
  const shareLink = typeof window !== 'undefined' ? `${window.location.origin}/experiments/${test?.id}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copy this experiment link:', shareLink);
    }
  };

  const declaredWinner = test?.variants?.find((variant) => variant.id === test?.winnerVariantId);
  const leader = !declaredWinner && isFormExperiment
    ? [...(test?.variants || [])]
      .filter((variant) => Number(variant?.metrics?.viewCount || 0) > 0)
      .sort((a, b) => Number(b?.metrics?.conversionRate || 0) - Number(a?.metrics?.conversionRate || 0))[0]
    : null;

  return (
    <article className="bg-card border border-border rounded-xl p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-lg font-semibold text-foreground">{test?.testName}</h3>
            <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusStyles[test?.status] || 'bg-muted text-muted-foreground'}`}>
              {test?.status}
            </span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{test?.description || (isFormExperiment ? 'Form comparison experiment' : 'Archived email A/B test')}</p>
        </div>
        <Icon name={isFormExperiment ? 'FlaskConical' : 'Archive'} size={20} className="shrink-0 text-muted-foreground" />
      </div>

      {isFormExperiment ? (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg bg-muted/50 p-3">
              <p className="text-xs text-muted-foreground">Views</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{test?.summary?.views || 0}</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-3">
              <p className="text-xs text-muted-foreground">Clicks</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{test?.summary?.clicks || 0}</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-3">
              <p className="text-xs text-muted-foreground">Submissions</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{test?.summary?.submissions || 0}</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-3">
              <p className="text-xs text-muted-foreground">Conversion</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{percent(test?.summary?.conversionRate)}</p>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {(test?.variants || []).map((variant) => (
              <div key={variant.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-foreground">{variant?.form?.title || variant?.variantName}</span>
                <span className="shrink-0 text-muted-foreground">{variant?.trafficPercentage}% traffic · {percent(variant?.metrics?.conversionRate)}</span>
              </div>
            ))}
          </div>

          {(declaredWinner || leader) && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
              <Icon name="Trophy" size={16} />
              <span>{declaredWinner ? 'Winner' : 'Current leader'}: <strong>{(declaredWinner || leader)?.form?.title || (declaredWinner || leader)?.variantName}</strong></span>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2">
            <Icon name="Link" size={15} className="text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{shareLink}</span>
            <button type="button" onClick={copyLink} className="text-xs font-medium text-primary hover:underline">
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </>
      ) : (
        <div className="mt-5 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          This is an archived email-based A/B test. It remains available for reference, but new tests compare forms.
        </div>
      )}

      <div className="mt-5 flex items-center gap-2">
        <Button onClick={() => onView(test)} className="flex-1">
          <Icon name="ChartNoAxesCombined" size={16} />
          View results
        </Button>
        {isFormExperiment && ['active', 'paused'].includes(test?.status) && (
          <button type="button" onClick={() => onPause(test)} className="rounded-lg border border-border p-2 hover:bg-muted" title={test?.status === 'paused' ? 'Resume experiment' : 'Pause experiment'}>
            <Icon name={test?.status === 'paused' ? 'Play' : 'Pause'} size={16} />
          </button>
        )}
        {test?.status !== 'completed' && (
          <button type="button" onClick={() => onComplete(test)} className="rounded-lg border border-border p-2 hover:bg-muted" title="Complete experiment">
            <Icon name="CheckCircle2" size={16} />
          </button>
        )}
        <button type="button" onClick={() => onDelete(test)} className="rounded-lg border border-border p-2 text-destructive hover:bg-destructive/10" title="Delete experiment">
          <Icon name="Trash2" size={16} />
        </button>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">Started {formatDate(test?.startDate)}</p>
    </article>
  );
};

export default TestCard;